import {
  fileNamePart,
  mdBlockquote,
  mdFrontmatter,
  mdHardBreaks,
  mdInline,
  mdJoinBlocks,
  mdLinkTarget,
  mdPlainText,
  mdTable,
  safeFileName,
} from '@shared/utils/markdown-export.utils';
import { PlatformIChingConsultationExportDto } from '@data/_generated-api/platform-api/models/platform-i-ching-consultation-export-dto';
import { MdPlanetRow, planetsMarkdown } from '../../../export/hd-export-planets.markdown';
import { IChingTranslate } from '../../consult/iching-consult-translate';
import { buildMovingRows, buildRootPairs, buildTrigramShift, listLines } from '../../consult/reading/iching-reading-derivations';
import { analyse, hexagramGlyph } from '../../core/iching-core';
import { ICHING_TRIGRAMS } from '../../core/iching-trigrams.const';
import {
  IChingHexagram,
  IChingIndexEntry,
  IChingLineValue,
  IChingMethodContent,
  IChingMethodStep,
  IChingMethodTrigram,
  IChingReading,
  IChingTrigramId,
} from '../../core/iching.models';
import { buildIChingTermMatcher, termsInText } from '../../reader/iching-terms.util';
import { parseCoinFaces, parseLines } from '../hd-iching-journal.util';

export interface IChingMarkdownInput {
  readonly consultation: PlatformIChingConsultationExportDto;
  /** Content van het primaire en het veranderde hexagram; ontbreekt bij een laadfout. */
  readonly primary?: IChingHexagram;
  readonly changed?: IChingHexagram;
  /** Methode-content (staptitels en uitleg, trigrammen, lijnposities, begrippen); ontbreekt bij een laadfout. */
  readonly method?: IChingMethodContent;
  /** Index van de bron, voor de titel van het kernhexagram. */
  readonly index?: readonly IChingIndexEntry[];
  readonly planets: readonly MdPlanetRow[];
  /** Lokaal opgemaakt tijdstip van de raadpleging (titelregel). */
  readonly dateText: string;
  /** ISO-tijdstip met lokale offset (frontmatter). */
  readonly dateIso: string;
  readonly revisitedText?: string;
  readonly exportedText: string;
  readonly exportedIso: string;
  /** Content-taal (lijsten als "2, 3 en 4"). */
  readonly lang: string;
  /** Vertaalfunctie in de content-taal; de export gebruikt dezelfde teksten als het leesscherm. */
  readonly t: IChingTranslate;
}

const YANG = '━━━━━━━━━';
const YIN = '━━━   ━━━';
/** Een vraag tot deze lengte (op één regel) wordt een kop; langer blijft een vette alinea. */
const QUESTION_HEADING_MAX = 200;

/**
 * Eén raadpleging als Markdown, in de opbouw van het leesscherm (spec
 * 2026-10-21-dagboek-momenten-export-design §2.2): frontmatter en titel met de hexagramtekens,
 * dan "1. Je vraag", "2. De worp" en "3. De duiding" met de zes stappen (trigrammen, bewegende
 * lijnen, wortel, tekst, omslaghexagram, praktijk), elk met de titel en de uitleg uit de methode,
 * en tot slot de planeetstanden. Zonder methode-content vallen titels en uitleg terug op het
 * minimum. Puur.
 */
export function buildConsultationMarkdown(input: IChingMarkdownInput): string {
  const { consultation: row, primary, changed, t } = input;
  const lines = parseLines(row.lines);
  const reading = lines.length === 6 ? analyse(lines) : undefined;
  const primaryNumber = reading?.primary ?? row.primaryHexagram ?? 0;
  const changedNumber = reading?.changed ?? row.changedHexagram ?? undefined;
  const question = (row.question ?? '').trim();
  const isSituation = row.questionKind === 'situation';

  const frontmatter = mdFrontmatter({
    type: 'classic-iching',
    date: input.dateIso,
    question,
    question_kind: row.questionKind ?? undefined,
    cast_mode: row.castMode ?? undefined,
    lines,
    hexagram: primaryNumber,
    hexagram_name: primary ? hexagramName(primary) : undefined,
    changed_hexagram: changedNumber,
    changed_hexagram_name: changed ? hexagramName(changed) : undefined,
    moving_lines: reading?.movingPositions ?? [],
    keywords: row.keywords ?? [],
    chart: row.linkedChartName ?? undefined,
    revisited: row.revisitedOn ?? undefined,
    source: primary ? `${primary.source.work} (${primary.source.translator})` : undefined,
    exported: input.exportedIso,
  });

  const castModes: Record<string, string> = {
    coins: t('humanDesign.iching.cast.modeCoins'),
    manual: t('humanDesign.iching.cast.modeManual'),
    direct: t('humanDesign.iching.cast.modeDirect'),
  };
  const meta = [
    `*${mdInline(input.dateText)}*`,
    t(isSituation ? 'humanDesign.iching.practice.text.situation' : 'humanDesign.iching.practice.text.question'),
    castModes[row.castMode ?? ''] ?? row.castMode ?? '',
    row.linkedChartName ? `${t('humanDesign.iching.export.md.chart')}: ${mdInline(row.linkedChartName)}` : '',
    input.revisitedText ? t('humanDesign.iching.journal.revisited', { date: input.revisitedText }) : '',
  ]
    .filter(part => part.length > 0)
    .join(' · ');

  const title = `# ${hexagramLabel(primaryNumber, primary)}${changedNumber ? ` → ${hexagramLabel(changedNumber, changed)}` : ''}`;

  return `${mdJoinBlocks([
    frontmatter,
    title,
    meta,
    questionPhase(question, input),
    castPhase(lines, reading, primaryNumber, input),
    reading ? interpretationPhase(reading, changedNumber, input) : '',
    input.planets.length > 0
      ? `## ${t('humanDesign.iching.moment.transitTitle')}\n\n${planetsMarkdown(input.planets, {
          planet: t('humanDesign.iching.export.md.planet'),
          gateLine: t('humanDesign.iching.export.md.gateLine'),
          retrograde: t('humanDesign.iching.export.md.retrograde'),
        })}`
      : '',
    footer(input),
  ])}\n`;
}

// ── 1. Je vraag ──────────────────────────────────────────────────────────

function questionPhase(question: string, input: IChingMarkdownInput): string {
  const { t } = input;
  let shown = '';
  if (question && !question.includes('\n') && question.length <= QUESTION_HEADING_MAX) {
    shown = `### ${mdInline(question)}`;
  } else if (question) {
    // Een lange vraag of een vraag over meerdere regels: vet, met de regels behouden.
    shown = mdPlainText(question)
      .split('\n')
      .map(line => (line.trim() ? `**${line.replace(/\s+$/, '')}**${line.endsWith('  ') ? '  ' : ''}` : line))
      .join('\n');
  }
  return mdJoinBlocks([`## ${t('humanDesign.iching.consult.phase1')}`, stepLead(input.method, 'question'), shown]);
}

// ── 2. De worp ───────────────────────────────────────────────────────────

function castPhase(lines: readonly IChingLineValue[], reading: IChingReading | undefined, primaryNumber: number, input: IChingMarkdownInput): string {
  const { t, method } = input;
  if (lines.length !== 6) return '';
  const figure = [...lines]
    .map((value, index) => {
      const glyph = value === 7 || value === 9 ? YANG : YIN;
      const marker = value === 9 ? '○' : value === 6 ? '✕' : ' ';
      const note = value === 9 ? `  ${t('humanDesign.iching.cast.lineMovingYang')}` : value === 6 ? `  ${t('humanDesign.iching.cast.lineMovingYin')}` : '';
      return `${index + 1}  ${glyph}  ${marker}  ${value}${note}`;
    })
    .reverse()
    .join('\n');
  const legend = method?.steps.steps.find(step => step.id === 'cast')?.legend;
  const legendList = legend
    ? (['6', '7', '8', '9'] as const).map(value => `- **${value}**: ${mdInline(legend.values[value])}`).join('\n')
    : '';
  const trigrams = reading
    ? `${t('humanDesign.iching.reader.above')} ${trigramLabel(reading.upper, method)}  \n${t('humanDesign.iching.reader.below')} ${trigramLabel(reading.lower, method)}`
    : '';
  return mdJoinBlocks([
    `## ${t('humanDesign.iching.consult.phase2')}`,
    stepLead(method, 'cast'),
    '```text\n' + figure + '\n```',
    legendList,
    `**${t('humanDesign.iching.export.md.yourHexagram')}:** ${hexagramLabel(primaryNumber, input.primary)}`,
    trigrams,
  ]);
}

// ── 3. De duiding ────────────────────────────────────────────────────────

function interpretationPhase(reading: IChingReading, changedNumber: number | undefined, input: IChingMarkdownInput): string {
  return mdJoinBlocks([
    `## ${input.t('humanDesign.iching.consult.phase3')}`,
    trigramsStep(reading, input),
    movingStep(reading, input),
    rootStep(reading, input),
    textStep(reading, input),
    changedNumber ? changeStep(reading, changedNumber, input) : '',
    practiceStep(input),
  ]);
}

/**
 * C1: het hexagram en zijn twee trigrammen, boven eerst (omgeving, het gevolg), dan onder (jij,
 * de oorzaak): zoals de kaarten op het scherm naast de figuur staan (Steven 2026-10-21).
 */
function trigramsStep(reading: IChingReading, input: IChingMarkdownInput): string {
  const { t, method } = input;
  const card = (id: IChingTrigramId, role: 'lower' | 'upper'): string => {
    const trigram = methodTrigram(id, method);
    const roleText = t(role === 'lower' ? 'humanDesign.iching.reading.roleLower' : 'humanDesign.iching.reading.roleUpper');
    const heading = `#### ${trigramLabel(id, method)}${trigram ? ` (${mdInline(trigram.wilhelmName)})` : ''}: ${roleText}`;
    if (!trigram) return heading;
    const extreme = reading.movingPositions.some(position => (role === 'lower' ? position <= 3 : position >= 4));
    return mdJoinBlocks([
      heading,
      mdPlainText(trigram.core),
      mdPlainText(role === 'lower' ? trigram.asLower : trigram.asUpper),
      `*${mdInline(trigram.family)}, ${mdInline(trigram.gender)}*`,
      extreme ? `> ${mdInline(trigram.extreme)}` : '',
    ]);
  };
  return mdJoinBlocks([
    stepHeading(method, 'trigrams'),
    stepLead(method, 'trigrams'),
    `**${hexagramLabel(reading.primary, input.primary)}**`,
    card(reading.upper, 'upper'),
    card(reading.lower, 'lower'),
  ]);
}

/** C2: evenwicht en concentratie, per bewegende lijn plaats, rol, herkomst, boodschap en waarschuwing. */
function movingStep(reading: IChingReading, input: IChingMarkdownInput): string {
  const { t, method } = input;
  const badges = [
    t(`humanDesign.iching.reading.balance.${reading.balance}`),
    reading.concentration !== 'none' ? t(`humanDesign.iching.reading.concentration.${reading.concentration}`) : '',
  ]
    .filter(part => part.length > 0)
    .map(part => `**${mdInline(part)}**`)
    .join(' · ');
  const rows = method
    ? buildMovingRows(reading, method, parseCoinFaces(input.consultation.coinFaces), t).map(row =>
        mdJoinBlocks([
          `#### ${t('humanDesign.iching.export.md.line', { line: row.position })}${row.method ? ` · ${mdInline(row.method.title)}` : ''}`,
          row.method ? `*${mdInline(row.method.role)} · ${t(`humanDesign.iching.reading.sancai.${row.method.sancai}`)}*` : '',
          mdInline(row.origin),
          row.method ? mdPlainText(row.method.movingMessage) : '',
          row.warning ? `> ${row.symbol ? `${row.symbol} ` : ''}${mdInline(row.warning)}` : '',
        ]),
      )
    : [];
  const summary = reading.movingLineTrigrams.length > 0
    ? `${t('humanDesign.iching.reading.summaryTrigrams')} ${reading.movingLineTrigrams.map(id => ICHING_TRIGRAMS[id].symbol).join(' ')}`
    : '';
  return mdJoinBlocks([
    stepHeading(method, 'moving'),
    stepLead(method, 'moving'),
    mdPlainText(t('humanDesign.iching.reading.movingOrigin')),
    badges,
    ...rows,
    summary,
    rows.length > 1 ? `*${t('humanDesign.iching.reading.oneByOne')}*` : '',
  ]);
}

/** C3: de wortel, de twee kerntrigrammen met wat ze omhult, en het kernhexagram. */
function rootStep(reading: IChingReading, input: IChingMarkdownInput): string {
  const { t, method, lang } = input;
  if (!method) return '';
  const pairs = buildRootPairs(reading, method).map(pair =>
    mdJoinBlocks([
      `#### ${t(pair.kind === 'upper' ? 'humanDesign.iching.reading.nuclearUpper' : 'humanDesign.iching.reading.nuclearLower')}: ${trigramLabel(pair.nuclearId, method)}`,
      `*${t('humanDesign.iching.reading.nuclearFrom', { lines: listLines(pair.fromLines, lang) })}*`,
      pair.nuclear ? mdPlainText(pair.kind === 'upper' ? pair.nuclear.asNuclearUpper : pair.nuclear.asNuclearLower) : '',
      `**${t('humanDesign.iching.reading.enveloping')}: ${trigramLabel(pair.envelopingId, method)}**  \n*${t('humanDesign.iching.reading.envelopingFrom', {
        lines: listLines(pair.envelopingLines, lang),
      })}*`,
      pair.enveloping ? mdPlainText(pair.enveloping.asEnveloping) : '',
      pair.envelopedBySelf ? `*${t('humanDesign.iching.reading.envelopedBySelf')}*` : '',
    ]),
  );
  const nuclearEntry = input.index?.find(entry => entry.number === reading.nuclearHexagram);
  const nuclear = mdJoinBlocks([
    `#### ${t('humanDesign.iching.reading.nuclearHexagram')}: ${hexagramGlyph(reading.nuclearHexagram)} ${reading.nuclearHexagram}${
      nuclearEntry ? ` ${mdInline(nuclearEntry.chineseName)} · ${mdInline(nuclearEntry.title)}` : ''
    }`,
    `*${t('humanDesign.iching.reading.nuclearHexagramFrom')}*`,
  ]);
  return mdJoinBlocks([stepHeading(method, 'nuclear'), stepLead(method, 'nuclear'), ...pairs, nuclear]);
}

/** C4: leeswijzer, begrippen in deze tekst, en de kern van de orakeltekst met "Lijn N." voor elke lijn. */
function textStep(reading: IChingReading, input: IChingMarkdownInput): string {
  const { t, method, primary } = input;
  const step = methodStep(method, 'text');
  const readingOrder = step?.readingOrder?.length
    ? `**${t('humanDesign.iching.reading.readingOrder')}**\n\n${step.readingOrder.map((item, index) => `${index + 1}. ${mdInline(item)}`).join('\n')}`
    : '';
  const moving = reading.movingPositions;
  const terms = primary && method
    ? termsInText(
        [...primary.text.judgement.text, ...primary.text.image.text, ...primary.text.lines.filter(line => moving.includes(line.position)).flatMap(line => line.text)].join(
          '\n',
        ),
        method.terms,
        buildIChingTermMatcher(method.terms),
      )
    : [];
  const termsBlock = terms.length > 0
    ? `**${t('humanDesign.iching.reading.terms')}**\n\n${terms.map(term => `- **${mdInline(term.term)}**: ${mdInline(term.short)}`).join('\n')}`
    : '';
  if (!primary) {
    return mdJoinBlocks([stepHeading(method, 'text'), stepLead(method, 'text'), readingOrder]);
  }
  const lineTexts = primary.text.lines
    .filter(line => moving.includes(line.position))
    .map(line => `**${t('humanDesign.iching.export.md.line', { line: line.position })}. ${mdInline(line.label)}**\n\n${verse(line.text)}`);
  // Alle zes lijnen bewegend bij 1 en 2: de tekst "louter negens/zessen" hoort er ook bij.
  const allLines = moving.length === 6 && primary.text.allLines;
  if (allLines) {
    lineTexts.push(`**${mdInline(primary.text.allLines!.label)}**\n\n${verse(primary.text.allLines!.text)}`);
  }
  return mdJoinBlocks([
    stepHeading(method, 'text'),
    stepLead(method, 'text'),
    readingOrder,
    termsBlock,
    allLines ? `*${t('humanDesign.iching.reading.allLinesWilhelm')}*` : '',
    `#### ${t('humanDesign.iching.reader.judgement')}\n\n${verse(primary.text.judgement.text)}`,
    `#### ${t('humanDesign.iching.reader.image')}\n\n${verse(primary.text.image.text)}`,
    lineTexts.length > 0 ? `#### ${t('humanDesign.iching.reader.lines')}\n\n${lineTexts.join('\n\n')}` : '',
  ]);
}

/** C5: het omslaghexagram, de trigramwissel met toelichting, oordeel en beeld, en de vaste regel. */
function changeStep(reading: IChingReading, changedNumber: number, input: IChingMarkdownInput): string {
  const { t, method, changed, lang } = input;
  const primaryNumber = reading.primary;
  const compare = `**${hexagramLabel(primaryNumber, input.primary)} ↔ ${hexagramLabel(changedNumber, changed)}**`;
  const shift = method ? buildTrigramShift(reading, method, lang, t) : undefined;
  const shifts = shift?.explanation
    ? mdJoinBlocks([
        shift.rows
          .map(row => {
            const label = t(row.kind === 'upper' ? 'humanDesign.iching.reading.shiftUpper' : 'humanDesign.iching.reading.shiftLower');
            const from = `${ICHING_TRIGRAMS[row.from].symbol} ${mdInline(row.fromName)}`;
            return row.changes
              ? `- **${label}**: ${from} → ${ICHING_TRIGRAMS[row.to].symbol} ${mdInline(row.toName)}`
              : `- **${label}**: ${ICHING_TRIGRAMS[row.from].symbol} ${mdInline(t('humanDesign.iching.reading.shiftStays', { name: row.fromName }))}`;
          })
          .join('\n'),
        mdPlainText(shift.explanation),
      ])
    : '';
  const rule = methodStep(method, 'change')?.rule;
  return mdJoinBlocks([
    stepHeading(method, 'change'),
    stepLead(method, 'change'),
    compare,
    shifts,
    changed ? `#### ${t('humanDesign.iching.reading.changedJudgement')}\n\n${verse(changed.text.judgement.text)}` : '',
    changed ? `#### ${t('humanDesign.iching.export.md.changedImage')}\n\n${verse(changed.text.image.text)}` : '',
    rule ? `*${mdInline(rule)}*` : '',
  ]);
}

/** C6: de eigen reflectie. */
function practiceStep(input: IChingMarkdownInput): string {
  const { t, method, consultation: row } = input;
  const inline = (items: readonly string[] | null | undefined): string[] => (items ?? []).map(item => mdInline(item)).filter(item => item.length > 0);
  const keywords = inline(row.keywords);
  const doList = inline(row.doList);
  const dontList = inline(row.dontList);
  const revisit = methodStep(method, 'practice')?.revisit;
  return mdJoinBlocks([
    stepHeading(method, 'practice'),
    stepLead(method, 'practice'),
    keywords.length > 0 ? `#### ${t('humanDesign.iching.practice.keywords')}\n\n${keywords.join(' · ')}` : '',
    doList.length > 0 ? `#### ${t('humanDesign.iching.practice.do')}\n\n${doList.map(item => `- ${item}`).join('\n')}` : '',
    dontList.length > 0 ? `#### ${t('humanDesign.iching.practice.dont')}\n\n${dontList.map(item => `- ${item}`).join('\n')}` : '',
    row.notes?.trim() ? `#### ${t('humanDesign.iching.practice.notes')}\n\n${mdPlainText(row.notes)}` : '',
    revisit ? `*${mdInline(revisit)}*` : '',
  ]);
}

// ── Bestand en index ─────────────────────────────────────────────────────

/** Hoeveel tekens van de vraag in de bestandsnaam komen (ingekort op een woordgrens). */
const FILE_NAME_QUESTION_MAX = 60;

/**
 * Bestandsnaam: `2026-10-21 1432 53-57 Begin van de vraag.md` (zonder botsingscontrole). Datum en
 * hexagrammen staan er altijd volledig in; enkel de vraag wordt ingekort en opgeschoond.
 */
export function consultationFileName(row: PlatformIChingConsultationExportDto, localStamp: string): string {
  const numbers = row.changedHexagram ? `${row.primaryHexagram}-${row.changedHexagram}` : `${row.primaryHexagram ?? ''}`;
  const question = fileNamePart(row.question, FILE_NAME_QUESTION_MAX);
  return safeFileName(`${localStamp} ${numbers} ${question}`.trim(), 'md');
}

/** Eén rij van `index.md`. */
export interface IChingJournalIndexRow {
  readonly fileName: string;
  readonly dateText: string;
  readonly hexagrams: string;
  readonly question: string;
}

export interface IChingIndexLabels {
  readonly title: string;
  readonly summary: string;
  readonly date: string;
  readonly hexagrams: string;
  readonly question: string;
  /** Koppen van de uitleg bij de stappen: sectietitel, inleiding en de drie fases. */
  readonly guideTitle: string;
  readonly guideIntro: string;
  readonly phases: readonly [string, string, string];
}

/**
 * `index.md` van een volledige dagboekexport: één tabelrij per raadpleging, met link, en daaronder
 * de uitleg bij de stappen. Die "Meer uitleg"-teksten zijn voor elke raadpleging dezelfde, dus ze
 * staan hier één keer in plaats van in elk bestand (Steven 2026-10-21).
 */
export function buildJournalIndexMarkdown(
  entries: readonly IChingJournalIndexRow[],
  labels: IChingIndexLabels,
  method?: IChingMethodContent,
): string {
  const table = mdTable(
    [labels.date, labels.hexagrams, labels.question],
    entries.map(entry => [`[${entry.dateText}](${mdLinkTarget(entry.fileName)})`, entry.hexagrams, entry.question]),
  );
  return `${mdJoinBlocks([`# ${labels.title}`, `*${labels.summary}*`, table, stepsGuide(labels, method)])}\n`;
}

/** Per fase de stappen met titel, korte uitleg en de alinea's van "Meer uitleg". */
function stepsGuide(labels: IChingIndexLabels, method: IChingMethodContent | undefined): string {
  if (!method) return '';
  const phaseOf = (id: IChingMethodStep['id']): number => (id === 'question' ? 0 : id === 'cast' ? 1 : 2);
  const phases = labels.phases.map((title, phase) => {
    const steps = method.steps.steps
      .filter(step => phaseOf(step.id) === phase)
      .map(step =>
        mdJoinBlocks([`#### ${mdInline(step.title)}`, `*${mdInline(step.lead)}*`, ...step.more.map(paragraph => mdPlainText(paragraph))]),
      );
    return steps.length > 0 ? mdJoinBlocks([`### ${mdInline(title)}`, ...steps]) : '';
  });
  const attribution = method.steps.attribution ? `*${mdInline(method.steps.attribution)}*` : '';
  return mdJoinBlocks([`## ${mdInline(labels.guideTitle)}`, `*${mdInline(labels.guideIntro)}*`, ...phases, attribution]);
}

/** Korte hexagramaanduiding voor lijsten: `䷴ 53 Jian → ䷸ 57 Xun`. */
export function hexagramsShort(primaryNumber: number, primary?: IChingHexagram, changedNumber?: number | null, changed?: IChingHexagram): string {
  const part = (number: number, hexagram?: IChingHexagram): string =>
    `${glyphOrEmpty(number)}${number}${hexagram ? ` ${hexagram.chineseName}` : ''}`;
  return changedNumber ? `${part(primaryNumber, primary)} → ${part(changedNumber, changed)}` : part(primaryNumber, primary);
}

// ── Kleine bouwstenen ────────────────────────────────────────────────────

function methodStep(method: IChingMethodContent | undefined, id: IChingMethodStep['id']): IChingMethodStep | undefined {
  return method?.steps.steps.find(step => step.id === id);
}

function stepHeading(method: IChingMethodContent | undefined, id: IChingMethodStep['id']): string {
  const step = methodStep(method, id);
  return step ? `### ${mdInline(step.title)}` : '';
}

/** De uitleg onder een staptitel, cursief zoals op het scherm. */
function stepLead(method: IChingMethodContent | undefined, id: IChingMethodStep['id']): string {
  const lead = methodStep(method, id)?.lead;
  return lead ? `*${mdInline(lead)}*` : '';
}

function methodTrigram(id: IChingTrigramId, method: IChingMethodContent | undefined): IChingMethodTrigram | undefined {
  return method?.trigrams.find(trigram => trigram.id === id);
}

/** `☴ Xùn · De Wind`; zonder methode-content enkel symbool en pinyin. */
function trigramLabel(id: IChingTrigramId, method: IChingMethodContent | undefined): string {
  const facts = ICHING_TRIGRAMS[id];
  const name = methodTrigram(id, method)?.name;
  return name ? `${facts.symbol} ${facts.pinyin} · ${mdInline(name)}` : `${facts.symbol} ${facts.pinyin}`;
}

function hexagramName(hexagram: IChingHexagram): string {
  return `${hexagram.chineseName} · ${hexagram.title}`;
}

/** `䷴ 53 Jian · De Ontwikkeling`, zonder content `䷴ 53`. */
function hexagramLabel(number: number, hexagram?: IChingHexagram): string {
  return `${glyphOrEmpty(number)}${number}${hexagram ? ` ${mdInline(hexagramName(hexagram))}` : ''}`;
}

function glyphOrEmpty(number: number): string {
  return number >= 1 && number <= 64 ? `${hexagramGlyph(number)} ` : '';
}

/** Orakeltekst als citaat; elke bronregel blijft een eigen regel. */
function verse(paragraphs: readonly string[]): string {
  return mdBlockquote(mdHardBreaks(paragraphs.map(paragraph => mdPlainText(paragraph)).join('\n')));
}

function footer(input: IChingMarkdownInput): string {
  const { t, primary, method } = input;
  const parts = [
    primary ? t('humanDesign.iching.export.md.source', { work: primary.source.work, translator: primary.source.translator, year: primary.source.year }) : '',
    (method?.steps.attribution ?? '').replace(/\.\s*$/, ''),
    t('humanDesign.iching.export.md.exported', { date: input.exportedText }),
  ].filter(part => part.length > 0);
  return `---\n\n*${parts.map(part => mdInline(part)).join(' · ')}*`;
}
